// W-0560 — Card de busca (Card 1 do Accordion).
// Radio com 3 modos (numero / cedente / banco_agencia_conta_numero) + Formik + Yup.
import React, { useState } from 'react';
import { Card, Form, Row, Col, Button, Table } from 'react-bootstrap';
import { Formik, Form as FormikForm } from 'formik';
import { FaSearch, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loading from '~/Components/Loading';
import {
  buscarChequesReapresentacao,
} from '~/Services/Service';
import {
  buscaChequesSchema,
} from './validationSchemas';
import {
  mensagens,
  MODO_NUMERO,
  MODO_CEDENTE,
  MODO_BANCO_AGENCIA,
  MODO_BACKEND,
} from './mensagens';

const formatarMoeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarData = (data) => {
  if (!data) return '';
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
};

const BuscaCheques = ({ geSelecionado, cheques, onAdicionar }) => {
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  const valoresIniciais = {
    modo: MODO_NUMERO,
    numero_cheque: '',
    cedente: '',
    banco: '',
    agencia: '',
    conta_cedente: '',
    numero_cheque_bac: '',
  };

  const handleBuscar = async (values, { setSubmitting }) => {
    if (!geSelecionado) {
      toast.info(mensagens.GE_OBRIGATORIA);
      setSubmitting(false);
      return;
    }

    const payload = {
      codigo_grupo_empresa: geSelecionado.codigo,
      modo: MODO_BACKEND[values.modo],
    };

    if (values.modo === MODO_NUMERO) {
      payload.numero_cheque = Number(values.numero_cheque);
    } else if (values.modo === MODO_CEDENTE) {
      payload.cedente = values.cedente;
    } else if (values.modo === MODO_BANCO_AGENCIA) {
      payload.banco = Number(values.banco);
      payload.agencia = Number(values.agencia);
      payload.conta_cedente = values.conta_cedente;
      payload.numero_cheque = Number(values.numero_cheque_bac);
    }

    setCarregando(true);
    try {
      const res = await buscarChequesReapresentacao(payload);
      const lista = Array.isArray(res) ? res : [];
      setResultados(lista);
      setBuscaRealizada(true);
      if (lista.length === 0) {
        toast.info('Nenhum cheque encontrado.');
      }
    } catch (err) {
      const msg = err && err.message ? err.message : mensagens.ERRO_BUSCA;
      toast.error(msg);
    } finally {
      setCarregando(false);
      setSubmitting(false);
    }
  };

  const tentarAdicionar = (item) => {
    const jaExiste = cheques.some(
      (c) => c.codigo_titulo === item.codigo_titulo
    );
    if (jaExiste) {
      toast.warning(mensagens.CHEQUE_JA_ADICIONADO);
      return;
    }
    onAdicionar(item);
  };

  return (
    <Card.Body>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={buscaChequesSchema}
        onSubmit={handleBuscar}
      >
        {({ values, errors, touched, handleChange, isSubmitting }) => (
          <FormikForm noValidate>
            <Row className="mb-2">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small mb-1 font-weight-bold">
                    Modo de busca
                  </Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      id="modo-numero"
                      label="Por número"
                      name="modo"
                      value={MODO_NUMERO}
                      checked={values.modo === MODO_NUMERO}
                      onChange={handleChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="modo-cedente"
                      label="Por cedente"
                      name="modo"
                      value={MODO_CEDENTE}
                      checked={values.modo === MODO_CEDENTE}
                      onChange={handleChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="modo-banco"
                      label="Por banco/agência/conta/número"
                      name="modo"
                      value={MODO_BANCO_AGENCIA}
                      checked={values.modo === MODO_BANCO_AGENCIA}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {values.modo === MODO_NUMERO && (
              <Row>
                <Col xs={12} md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">
                      Número do cheque
                    </Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name="numero_cheque"
                      value={values.numero_cheque}
                      onChange={handleChange}
                      isInvalid={
                        !!(touched.numero_cheque && errors.numero_cheque)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numero_cheque}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {values.modo === MODO_CEDENTE && (
              <Row>
                <Col xs={12} md={8}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Cedente</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="cedente"
                      value={values.cedente}
                      onChange={handleChange}
                      placeholder="Digite ao menos 3 caracteres do nome"
                      isInvalid={!!(touched.cedente && errors.cedente)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cedente}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {values.modo === MODO_BANCO_AGENCIA && (
              <Row>
                <Col xs={12} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Banco</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name="banco"
                      value={values.banco}
                      onChange={handleChange}
                      isInvalid={!!(touched.banco && errors.banco)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.banco}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Agência</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name="agencia"
                      value={values.agencia}
                      onChange={handleChange}
                      isInvalid={!!(touched.agencia && errors.agencia)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.agencia}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">
                      Conta cedente
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="conta_cedente"
                      value={values.conta_cedente}
                      onChange={handleChange}
                      isInvalid={
                        !!(touched.conta_cedente && errors.conta_cedente)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.conta_cedente}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">
                      Número do cheque
                    </Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name="numero_cheque_bac"
                      value={values.numero_cheque_bac}
                      onChange={handleChange}
                      isInvalid={
                        !!(touched.numero_cheque_bac &&
                          errors.numero_cheque_bac)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numero_cheque_bac}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mt-2">
              <Col xs={12}>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={isSubmitting || carregando || !geSelecionado}
                >
                  <FaSearch className="mr-1" /> Buscar
                </Button>
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>

      {carregando && (
        <div className="py-3">
          <Loading />
        </div>
      )}

      {!carregando && buscaRealizada && resultados.length > 0 && (
        <div className="mt-3">
          <h6 className="mb-2">Resultados ({resultados.length})</h6>
          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cedente</th>
                  <th>Vencimento</th>
                  <th className="text-right">Valor</th>
                  <th>Portador</th>
                  <th>Conta cedente</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((item) => (
                  <tr key={item.codigo_titulo}>
                    <td>{item.numero_cheque}</td>
                    <td>{item.cedente}</td>
                    <td>{formatarData(item.data_vencimento)}</td>
                    <td className="text-right">{formatarMoeda(item.valor)}</td>
                    <td>{item.portador}</td>
                    <td>{item.conta_cedente}</td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => tentarAdicionar(item)}
                      >
                        <FaPlus className="mr-1" /> Adicionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {!carregando && buscaRealizada && resultados.length === 0 && (
        <div className="mt-3 text-muted small">
          Nenhum cheque encontrado para o filtro informado.
        </div>
      )}
    </Card.Body>
  );
};

export default BuscaCheques;
